import { useState } from 'react';
import { useSEO } from '../../hooks/useSEO';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://yourmindshift24.com';

export default function KontaktPage() {
  useSEO({
    title: 'Kontakt – YourMindShift24 | Future Now UG, Aschheim',
    description: 'Kontaktiere YourMindShift24: Fragen, Kooperationen und Whitelabel-Anfragen an Future Now UG, Max-Planck-Str. 4, 85609 Aschheim. Wir antworten innerhalb von 24 Stunden.',
    keywords: 'YourMindShift24 Kontakt, Future Now UG, KI-Coach Kontakt, Kooperation, Whitelabel Anfrage',
    canonical: '/kontakt',
    ogTitle: 'Kontakt – YourMindShift24 | Fragen & Kooperationen',
    ogDescription: 'Kontaktiere unser Team bei Future Now UG für Fragen, Kooperationen und Whitelabel-Anfragen zu YourMindShift24.',
    ogImage: `${SITE_URL}/og-image.jpg`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Kontakt – YourMindShift24',
        url: `${SITE_URL}/kontakt`,
        description: 'Kontaktseite von YourMindShift24 – Future Now UG',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Future Now UG',
        url: SITE_URL,
        telephone: '',
        email: 'olaf.niggemann@icloud.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Max-Planck-Str. 4',
          addressLocality: 'Aschheim',
          postalCode: '85609',
          addressRegion: 'Bayern',
          addressCountry: 'DE',
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
        ],
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 48.1685,
          longitude: 11.7137,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Ist YourMindShift24 wirklich anonym?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Ja, absolut. Wir speichern keine persönlichen Daten und die Kommunikation erfolgt vollständig anonym über Telegram.',
            },
          },
          {
            '@type': 'Question',
            name: 'Was kostet YourMindShift24?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Die ersten 7 Tage sind komplett kostenlos. Danach kostet der Service 19€ pro Monat. Sie können jederzeit kündigen.',
            },
          },
          {
            '@type': 'Question',
            name: 'Wie schnell erhalte ich eine Antwort?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Der KI-Bot antwortet sofort. Bei persönlichen Anfragen per E-Mail antworten wir innerhalb von 24 Stunden.',
            },
          },
          {
            '@type': 'Question',
            name: 'Kann ich den Service jederzeit beenden?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Ja, Sie können die Nutzung jederzeit beenden. Es gibt keine Vertragsbindung oder Kündigungsfristen.',
            },
          },
          {
            '@type': 'Question',
            name: 'Ist meine Zahlung sicher?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Ja, alle Zahlungen werden über Stripe verarbeitet, einem der sichersten Zahlungsanbieter weltweit. Ihre Zahlungsdaten werden verschlüsselt übertragen und nicht bei uns gespeichert.',
            },
          },
        ],
      },
    ],
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    if (formData.message.length > 500) {
      alert('Die Nachricht darf maximal 500 Zeichen enthalten.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/submit/kontakt-form-67a0b8e4-8b2c-4f3a-9d1e-2f5c8a7b9e3d', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Kontakt</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Haben Sie Fragen zu Mindshift AI oder benötigen Sie Unterstützung? 
            Wir sind gerne für Sie da.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Kontaktinformationen */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-8">Kontaktinformationen</h2>
            
            <div className="space-y-8">
              <div className="bg-teal-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full mr-3">
                    <i className="ri-building-line text-sm"></i>
                  </div>
                  Unternehmen
                </h3>
                <div className="text-gray-700 leading-relaxed">
                  <p className="font-medium">Future Now UG (haftungsbeschränkt)</p>
                  <p>Max-Planck-Str. 4</p>
                  <p>85609 Aschheim</p>
                  <p>Deutschland</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-600 text-white rounded-full mr-3">
                    <i className="ri-mail-line text-sm"></i>
                  </div>
                  E-Mail
                </h3>
                <a 
                  href="mailto:olaf.niggemann@icloud.com" 
                  className="text-teal-600 hover:text-teal-700 transition-colors"
                >
                  olaf.niggemann@icloud.com
                </a>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-full mr-3">
                    <i className="ri-telegram-line text-sm"></i>
                  </div>
                  Mindshift AI Bot
                </h3>
                <p className="text-gray-700 mb-3">
                  Starten Sie direkt eine Unterhaltung mit unserem KI-Coach:
                </p>
                <a 
                  href="https://t.me/mindshift_ai_bot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-telegram-line mr-2"></i>
                  Telegram Bot öffnen
                </a>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Geschäftszeiten</h3>
              <div className="text-gray-700 space-y-1">
                <p><span className="font-medium">Montag - Freitag:</span> 9:00 - 18:00 Uhr</p>
                <p><span className="font-medium">Wochenende:</span> Nach Vereinbarung</p>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                * Der Mindshift AI Bot ist 24/7 verfügbar
              </p>
            </div>
          </div>

          {/* Kontaktformular */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-8">Nachricht senden</h2>
            
            <form 
              id="kontakt-form" 
              data-readdy-form 
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="ihre.email@beispiel.de"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Betreff
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Worum geht es?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Nachricht *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Ihre Nachricht an uns..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 Zeichen
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-600 mr-2"></i>
                    <p className="text-green-800">
                      Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns bald bei Ihnen.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 mr-2"></i>
                    <p className="text-red-800">
                      Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es erneut.
                    </p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !formData.name || !formData.email || !formData.message}
                className="w-full bg-teal-600 text-white py-3 px-6 rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Wird gesendet...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-send-plane-line mr-2"></i>
                    Nachricht senden
                  </span>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Mit dem Absenden stimmen Sie unserer{' '}
                <a href="/datenschutz" className="text-teal-600 hover:text-teal-700 underline">
                  Datenschutzerklärung
                </a>{' '}
                zu.
              </p>
            </form>
          </div>
        </div>

        {/* Zusätzliche Informationen */}
        <div className="mt-20 bg-gray-50 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Häufig gestellte Fragen</h2>
            <p className="text-gray-600">
              Hier finden Sie Antworten auf die wichtigsten Fragen zu Mindshift AI
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Ist Mindshift AI wirklich anonym?
              </h3>
              <p className="text-gray-600">
                Ja, absolut. Wir speichern keine persönlichen Daten und die Kommunikation 
                erfolgt vollständig anonym über Telegram.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Wie schnell erhalte ich eine Antwort?
              </h3>
              <p className="text-gray-600">
                Der KI-Bot antwortet sofort. Bei persönlichen Anfragen per E-Mail 
                antworten wir innerhalb von 24 Stunden.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Was kostet Mindshift AI?
              </h3>
              <p className="text-gray-600">
                Die ersten 7 Tage sind komplett kostenlos. Danach kostet der Service 
                19€ pro Monat. Sie können jederzeit kündigen.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Welche Zahlungsmöglichkeiten gibt es?
              </h3>
              <p className="text-gray-600">
                Die Zahlung wird sicher über Stripe abgewickelt. Sie können mit 
                Kreditkarte, Debitkarte, SEPA-Lastschrift, Apple Pay, Google Pay 
                oder anderen lokalen Zahlungsmethoden bezahlen.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Kann ich den Service jederzeit beenden?
              </h3>
              <p className="text-gray-600">
                Ja, Sie können die Nutzung jederzeit beenden. Es gibt keine 
                Vertragsbindung oder Kündigungsfristen.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Ist meine Zahlung sicher?
              </h3>
              <p className="text-gray-600">
                Ja, alle Zahlungen werden über Stripe verarbeitet, einem der 
                sichersten Zahlungsanbieter weltweit. Ihre Zahlungsdaten werden 
                verschlüsselt übertragen und nicht bei uns gespeichert.
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}